import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

/** Ch 27.2 — Plumbing Layout: fixture zones + tanks + sizing summary. */
export default function PlumbingLayoutPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [palette, setPalette] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");
  const [editFx, setEditFx] = useState({}); // zone_id -> {fixtures}

  const reload = useCallback(async () => {
    try {
      const [m, p, s] = await Promise.all([
        api.get(`/projects/${projectId}/mep-re/model`),
        api.get(`/projects/${projectId}/mep-re/palette`),
        api.get(`/projects/${projectId}/mep-re/plumbing/sizing-summary`),
      ]);
      setModel(m.data.model);
      setPalette(p.data);
      setSummary(s.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const addZone = async () => {
    const n = (model?.plumbing_zones?.length || 0) + 1;
    await api.post(`/projects/${projectId}/mep-re/plumbing/zones`,
      { name: `Bathroom ${n}`, level: "GF",
        fixtures: { wc_flush_tank: 1, lavatory: 1, shower: 1 } });
    reload();
  };
  const delZone = async (zid) => {
    await api.delete(`/projects/${projectId}/mep-re/plumbing/zones/${zid}`);
    reload();
  };
  const addTank = async (type) => {
    await api.post(`/projects/${projectId}/mep-re/plumbing/tanks`,
      { type, population: 100, demand_lpcd: 135, building_type: "residential" });
    reload();
  };
  const delTank = async (tid) => {
    await api.delete(`/projects/${projectId}/mep-re/plumbing/tanks/${tid}`);
    reload();
  };

  return (
    <div data-testid="plumbing-layout" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Fixtures + tank palette */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={label}>Fixture Catalogue</div>
        <table style={{ width: "100%", marginTop: 8, fontSize: 10 }}>
          <thead><tr><th style={{ textAlign: "left", color: "var(--pf-muted)" }}>Fixture</th>
            <th style={{ color: "var(--pf-muted)" }}>FU</th>
            <th style={{ color: "var(--pf-muted)" }}>DFU</th></tr></thead>
          <tbody>
            {(palette?.plumbing_fixtures || []).map(f => (
              <tr key={f.id}>
                <td style={{ padding: 2 }}>{f.label}</td>
                <td style={{ padding: 2, textAlign: "center" }}>{f.fu_supply}</td>
                <td style={{ padding: 2, textAlign: "center" }}>{f.dfu}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ ...label, marginTop: 14 }}>Add Tank</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
          <button data-testid="add-tank-overhead" style={btnGhost}
            onClick={() => addTank("overhead")}>+ Overhead Tank</button>
          <button data-testid="add-tank-sump" style={btnGhost}
            onClick={() => addTank("sump")}>+ Underground Sump</button>
        </div>
      </div>

      {/* Zones + tanks */}
      <div style={{ display: "grid", gridTemplateRows: "1fr auto", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={label}>Plumbing Zones (Hunter / DFU sizing)</div>
            <button data-testid="add-plumbing-zone" style={btn} onClick={addZone}>+ Add Zone</button>
          </div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Name","Level","FU","Q lps","Supply mm","V m/s","DFU","Stack mm","Vent mm",""]
                .map(h => <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(model?.plumbing_zones || []).map(z => {
                const r = summary?.zones?.find(s => s.zone_id === z.id) || {};
                return (
                  <tr key={z.id} data-testid={`plb-zone-${z.id}`}>
                    <td style={{ padding: 4 }}>{z.name}</td>
                    <td style={{ padding: 4 }}>{z.level}</td>
                    <td style={{ padding: 4 }}>{r.total_FU ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.demand_lps ?? "—"}</td>
                    <td style={{ padding: 4, fontWeight: 600 }}>{r.supply_pipe_mm ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.supply_velocity_ms ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.total_DFU ?? "—"}</td>
                    <td style={{ padding: 4, fontWeight: 600 }}>{r.stack_diameter_mm ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.vent_diameter_mm ?? "—"}</td>
                    <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                      onClick={() => delZone(z.id)}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Tanks */}
        <div style={{ ...card, padding: 12, maxHeight: 220, overflowY: "auto" }}>
          <div style={label}>Water Tanks (IS 1172)</div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Type","Pop","Demand l/d","Sump l","OHT l","Fire Reserve l","Pump lps",""]
                .map(h => <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(model?.plumbing_tanks || []).map(t => {
                const r = summary?.tanks?.find(s => s.tank_id === t.id) || {};
                return (
                  <tr key={t.id} data-testid={`plb-tank-${t.id}`}>
                    <td style={{ padding: 4 }}>{t.type}</td>
                    <td style={{ padding: 4 }}>{t.population}</td>
                    <td style={{ padding: 4 }}>{r.daily_demand_l ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.sump_l ?? "—"}</td>
                    <td style={{ padding: 4, fontWeight: 600 }}>{r.oht_l ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.fire_reserve_l ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.pump_lps ?? "—"}</td>
                    <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                      onClick={() => delTank(t.id)}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}
